import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { KeycloakService } from '../keycloak.service';
import { TablerIconsModule } from "angular-tabler-icons";
import { MatSnackBar } from '@angular/material/snack-bar';
import { RoleDTO } from '../models/role.dto';
import { RoleMappingDTO } from '../models/role-mapping.dto';
import { LocalizationPipe } from '../../../../pipe/localization-pipe';
import { LocalizationService } from '../../../../services/localizationService';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';


@Component({
  selector: 'app-role-mapping',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    FormsModule,
    TablerIconsModule,
    LocalizationPipe
  ],
  template: `
    <div class="row">
      <div class="col-6">
        <mat-card class="mb-4">
          <mat-card-content>
            <h2 class="mb-4">{{ 'KeyCloak.RoleMapping.Title' | localize }}</h2>
            <div class="mt-4">
              <div *ngFor="let role of roles" class="p-3 border rounded mb-2">
                <div class="d-flex justify-content-between align-items-center">
                  <div class="fs-6 fw-medium">{{ role.name }}</div>
                  <div>
                    <button mat-mini-fab color="primary" (click)="toggleRoleInput(role)">
                      <i-tabler name="plus" class="icon-20"></i-tabler>
                    </button>
                  </div>
                </div>

                <div *ngIf="roleMappings[role.id]?.showInput" class="d-flex gap-3 mt-3">
                  <mat-form-field appearance="outline" class="flex-grow-1">
                    <mat-label>{{ 'KeyCloak.RoleMapping.MapToRole' | localize }}</mat-label>
                    <input matInput [(ngModel)]="roleMappings[role.id].newRole"
                           placeholder="{{ 'KeyCloak.RoleMapping.EnterRoleName' | localize }}">
                  </mat-form-field>
                  <button mat-raised-button color="primary" (click)="addRoleMapping(role)">
                    {{ 'KeyCloak.Actions.Add' | localize }}
                  </button>
                </div>

                <div *ngIf="getMappedRoles(role.id).length > 0" class="mt-3">
                  <div *ngFor="let mappedRole of getMappedRoles(role.id)" class="d-flex justify-content-between align-items-center p-2 bg-light rounded mb-2">
                    {{ mappedRole }}
                    <button mat-icon-button color="warn" (click)="removeRoleMapping(role.id, mappedRole)">
                      <i-tabler name="trash" class="icon-20"></i-tabler>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </mat-card-content>
        </mat-card>
      </div>

      <div class="col-6">
        <mat-card class="mb-4">
          <mat-card-content>
            <h2 class="mb-4">{{ 'KeyCloak.RoleMapping.GroupMapping' | localize }}</h2>
            <div class="mt-4">
              <div *ngFor="let role of roles" class="p-3 border rounded mb-2">
                <div class="d-flex justify-content-between align-items-center">
                  <div class="fs-6 fw-medium">{{ role.name }}</div>
                  <div>
                    <button mat-mini-fab color="primary" (click)="toggleGroupInput(role)">
                      <i-tabler name="plus" class="icon-20"></i-tabler>
                    </button>
                  </div>
                </div>

                <div *ngIf="roleMappings[role.id]?.showInput" class="d-flex gap-3 mt-3">
                  <mat-form-field appearance="outline" class="flex-grow-1">
                    <mat-label>{{ 'KeyCloak.RoleMapping.MapToGroup' | localize }}</mat-label>
                    <input matInput [(ngModel)]="roleMappings[role.id].newGroup"
                           placeholder="{{ 'KeyCloak.RoleMapping.EnterGroupName' | localize }}">
                  </mat-form-field>
                  <button mat-raised-button color="primary" (click)="addGroupMapping(role)">
                    {{ 'KeyCloak.Actions.Add' | localize }}
                  </button>
                </div>

                <div *ngIf="getMappedGroups(role.id).length > 0" class="mt-3">
                  <div *ngFor="let mappedGroup of getMappedGroups(role.id)" class="d-flex justify-content-between align-items-center p-2 bg-light rounded mb-2">
                    {{ mappedGroup }}
                    <button mat-icon-button color="warn" (click)="removeGroupMapping(role.id, mappedGroup)">
                      <i-tabler name="trash" class="icon-20"></i-tabler>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </mat-card-content>
        </mat-card>
      </div>

      <div class="col-12">
        <div class="mt-4 d-flex justify-content-end">
          <button mat-raised-button color="primary" (click)="saveAllMappings()">
            {{ 'KeyCloak.RoleMapping.SaveAllMappings' | localize }}
          </button>
        </div>
      </div>
    </div>
  `,
  styles: []
})
export class RoleMappingComponent implements OnInit {
  roles: RoleDTO[] = [];
  roleMappings: { [key: number]: { showInput: boolean, newRole: string, newGroup: string } } = {};
  mappingsList: RoleMappingDTO[] = [];
  subdomain: string;

  constructor(
    private keycloakService: KeycloakService,
    private snackBar: MatSnackBar,
    private localizationService: LocalizationService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    // Query parametrelerinden subdomain'i al
    this.route.queryParams.subscribe(params => {
      this.subdomain = params['subdomain'] || window.location.hostname;
    });

    this.loadRoles();
  }

  loadRoles() {
    this.keycloakService.getAllRoles().subscribe({
      next: (roles) => {
        this.roles = roles;
        roles.forEach(role => {
          // Initialize role mappings
          this.roleMappings[role.id] = {
            showInput: false,
            newRole: '',
            newGroup: ''
          };
          this.mappingsList.push(new RoleMappingDTO({
            roleId: role.id,
            roleName: role.name,
            mappedRoles: [],
            mappedGroups: []
          }));
        });
      },
      error: (error) => {
        console.error('Error loading roles', error);
        this.snackBar.open(
          this.localizationService.getLocalizationValue('KeyCloak.Messages.LoadRolesError'),
          this.localizationService.getLocalizationValue('KeyCloak.Messages.Close'),
          {
            duration: 5000,
            panelClass: ['error-snackbar']
          }
        );
      }
    });
  }

  // Role Mapping Methods
  toggleRoleInput(role: RoleDTO) {
    if (this.roleMappings[role.id]) {
      this.roleMappings[role.id].showInput = !this.roleMappings[role.id].showInput;
      this.roleMappings[role.id].newRole = '';
    }
  }

  addRoleMapping(role: RoleDTO) {
    const mapping = this.roleMappings[role.id];
    if (mapping && mapping.newRole.trim()) {
      const roleMapping = this.mappingsList.find(m => m.roleId === role.id);
      if (roleMapping) {
        const newRole = mapping.newRole.trim();
        if (roleMapping.mappedRoles.includes(newRole)) {
          this.snackBar.open(
            this.localizationService.getLocalizationValue('KeyCloak.Messages.RoleAlreadyMapped'),
            this.localizationService.getLocalizationValue('KeyCloak.Messages.Close'),
            {
              duration: 3000,
              panelClass: ['error-snackbar']
            }
          );
          return;
        }
        roleMapping.addRoleMapping(newRole);
        mapping.newRole = '';
      }
    }
  }

  // Group Mapping Methods
  toggleGroupInput(role: RoleDTO) {
    if (this.roleMappings[role.id]) {
      this.roleMappings[role.id].showInput = !this.roleMappings[role.id].showInput;
      this.roleMappings[role.id].newGroup = '';
    }
  }

  addGroupMapping(role: RoleDTO) {
    const mapping = this.roleMappings[role.id];
    if (mapping && mapping.newGroup.trim()) {
      const roleMapping = this.mappingsList.find(m => m.roleId === role.id);
      if (roleMapping) {
        const newGroup = mapping.newGroup.trim();
        if (roleMapping.mappedGroups.includes(newGroup)) {
          this.snackBar.open(
            this.localizationService.getLocalizationValue('KeyCloak.Messages.GroupAlreadyMapped'),
            this.localizationService.getLocalizationValue('KeyCloak.Messages.Close'),
            {
              duration: 3000,
              panelClass: ['error-snackbar']
            }
          );
          return;
        }
        roleMapping.addGroupMapping(newGroup);
        mapping.newGroup = '';
      }
    }
  }

  removeRoleMapping(roleId: number, mappedRole: string) {
    const roleMapping = this.mappingsList.find(m => m.roleId === roleId);
    if (roleMapping) {
      roleMapping.removeRoleMapping(mappedRole);
    }
  }

  removeGroupMapping(roleId: number, mappedGroup: string) {
    const roleMapping = this.mappingsList.find(m => m.roleId === roleId);
    if (roleMapping) {
      roleMapping.removeGroupMapping(mappedGroup);
    }
  }

  getMappedRoles(roleId: number): string[] {
    const roleMapping = this.mappingsList.find(m => m.roleId === roleId);
    return roleMapping ? roleMapping.mappedRoles : [];
  }

  getMappedGroups(roleId: number): string[] {
    const roleMapping = this.mappingsList.find(m => m.roleId === roleId);
    return roleMapping ? roleMapping.mappedGroups : [];
  }

  saveAllMappings() {
    const mappingsToSave = this.mappingsList
      .filter(mapping => mapping.mappedRoles.length > 0 || mapping.mappedGroups.length > 0)
      .map(mapping => new RoleMappingDTO({
        roleId: mapping.roleId,
        roleName: mapping.roleName,
        mappedRoles: mapping.mappedRoles,
        mappedGroups: mapping.mappedGroups
      }));


    if (mappingsToSave.length > 0) {
      this.keycloakService.saveRoleMappings(mappingsToSave, this.subdomain).subscribe({
        next: (response) => {
          this.snackBar.open(
            this.localizationService.getLocalizationValue('KeyCloak.Messages.MappingsSaved'),
            this.localizationService.getLocalizationValue('KeyCloak.Messages.Close'),
            {
              duration: 3000,
              horizontalPosition: 'end',
              verticalPosition: 'top',
              panelClass: ['success-snackbar']
            }
          );
          this.clearAllMappings();
          this.router.navigate(['/dashboard']);
        },
        error: (error) => {
          this.snackBar.open(
            this.localizationService.getLocalizationValue('KeyCloak.Messages.MappingsError'),
            this.localizationService.getLocalizationValue('KeyCloak.Messages.Close'),
            {
              duration: 5000,
              horizontalPosition: 'end',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            }
          );
        }
      });
    }
  }

  clearAllMappings() {
    this.mappingsList.forEach(mapping => {
      mapping.mappedRoles = [];
      mapping.mappedGroups = [];
    });
    Object.values(this.roleMappings).forEach(mapping => {
      mapping.newRole = '';
      mapping.newGroup = '';
      mapping.showInput = false;
    });
  }
}
